import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import { devportalApi } from '../../../utils/devportalApi';

const toNullableInt = (v) => {
  if (v === '' || v === null || v === undefined) return null;
  const n = parseInt(String(v), 10);
  return Number.isFinite(n) ? n : null;
};

const numberText = (v) => (v === null || v === undefined ? '' : String(v));

// Helper function to calculate percentage
const calculatePercentage = (value, total) => {
  if (!total || total <= 0) return '0%';
  return `${Math.round((value / total) * 100)}%`;
};

const calculateQuotaPercentages = (projectQuotas, tenantLimit, type) => {
  const totalAllocated = Object.values(projectQuotas).reduce((sum, q) => {
    const limit = q[`${type}_limit`];
    return sum + (limit && !isNaN(limit) ? parseInt(limit) : 0);
  }, 0);

  return Object.entries(projectQuotas).reduce((acc, [id, q]) => {
    const limit = q[`${type}_limit`];
    const value = limit && !isNaN(limit) ? parseInt(limit) : 0;
    const percentage = tenantLimit > 0 ? (value / tenantLimit) * 100 : 0;
    acc[id] = {
      value,
      percentage: Math.round(percentage * 100) / 100, // Round to 2 decimal places
      remaining: tenantLimit - totalAllocated
    };
    return acc;
  }, {});
};

const ManageQuota = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [editingProjects, setEditingProjects] = useState({});

  const [nowTs, setNowTs] = useState(() => Date.now());
  const [quotaLoadedAtTs, setQuotaLoadedAtTs] = useState(null);
  const [hasActiveQuota, setHasActiveQuota] = useState(true);

  const [tenantQuota, setTenantQuota] = useState({
    message_limit: null,
    member_limit: null,
    message_used: null,
    member_used: null,
    message_remains: null,
    member_remains: null,
    quata_started: null,
    quata_ended: null,
    quata_remaining: null
  });

  const [projects, setProjects] = useState([]);
  const [projectQuotas, setProjectQuotas] = useState({});
  const [projectQuotasBaseline, setProjectQuotasBaseline] = useState({});
  const [quotaPercentages, setQuotaPercentages] = useState({
    message: {},
    member: {}
  });

  const projectIds = useMemo(() => projects.map(p => p?.id).filter(Boolean), [projects]);

  const updateQuotaPercentages = () => {
    if (!tenantQuota) return;
    
    setQuotaPercentages({
      message: calculateQuotaPercentages(
        projectQuotas, 
        tenantQuota.message_limit || 0, 
        'message'
      ),
      member: calculateQuotaPercentages(
        projectQuotas, 
        tenantQuota.member_limit || 0, 
        'member'
      )
    });
  };

  // Call this whenever projectQuotas or tenantQuota changes
  useEffect(() => {
    updateQuotaPercentages();
  }, [projectQuotas, tenantQuota]);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [quotaResponse, projs] = await Promise.all([
        devportalApi.getTenantQuota(),
        devportalApi.listProjects()
      ]);

      const quota = quotaResponse?.data;
      setHasActiveQuota(quota?.has_active_quota !== false); // Default to true if undefined
      setTenantQuota({
        message_limit: quota?.message_limit ?? null,
        member_limit: quota?.member_limit ?? null,
        message_used: quota?.message_used ?? null,
        member_used: quota?.member_used ?? null,
        message_remains: quota?.message_remains ?? null,
        member_remains: quota?.member_remains ?? null,
        quata_started: quota?.quata_started ?? null,
        quata_ended: quota?.quata_ended ?? null,
        quata_remaining: quota?.quata_remaining ?? null
      });
      setQuotaLoadedAtTs(Date.now());

      setProjects(Array.isArray(projs) ? projs : []);

      try {
        const ids = (Array.isArray(projs) ? projs : []).map((p) => p?.id).filter(Boolean);
        const rows = await Promise.all(
          ids.map(async (projectId) => {
            try {
              const q = await devportalApi.getProjectQuota(projectId);
              return {
                project_id: projectId,
                message_limit: q?.message_limit ?? null,
                member_limit: q?.member_limit ?? null
              };
            } catch (_) {
              return {
                project_id: projectId,
                message_limit: null,
                member_limit: null
              };
            }
          })
        );

        const map = {};
        rows.forEach((row) => {
          if (!row?.project_id) return;
          map[row.project_id] = {
            message_limit: row?.message_limit ?? null,
            member_limit: row?.member_limit ?? null
          };
        });
        setProjectQuotas(map);
        setProjectQuotasBaseline(map);
      } catch (_) {
        setProjectQuotas({});
        setProjectQuotasBaseline({});
      }
    } catch (e) {
      setError(e?.message || 'Failed to load quota data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const fillMissing = async () => {
      const missing = projectIds.filter((id) => projectQuotas[id] === undefined);
      if (missing.length === 0) return;
      const next = { ...projectQuotas };
      const nextBaseline = { ...projectQuotasBaseline };
      for (const id of missing) {
        try {
          const q = await devportalApi.getProjectQuota(id);
          next[id] = {
            message_limit: q?.message_limit ?? null,
            member_limit: q?.member_limit ?? null
          };
          nextBaseline[id] = {
            message_limit: q?.message_limit ?? null,
            member_limit: q?.member_limit ?? null
          };
        } catch (_) {
          next[id] = {
            message_limit: null,
            member_limit: null
          };
          nextBaseline[id] = {
            message_limit: null,
            member_limit: null
          };
        }
      }
      setProjectQuotas(next);
      setProjectQuotasBaseline(nextBaseline);
    };
    fillMissing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectIds.join(',')]);

  const setProjectQuotaField = (projectId, field, value) => {
    setProjectQuotas((prev) => {
      const existing = prev[projectId] || { message_limit: null, member_limit: null };
      return {
        ...prev,
        [projectId]: { ...existing, [field]: value }
      };
    });
  };

  const startEditProject = (projectId) => {
    setEditingProjects((prev) => ({ ...prev, [projectId]: true }));
  };

  const cancelEditProject = (projectId) => {
    setProjectQuotas((prev) => {
      const baseline = projectQuotasBaseline[projectId] || { message_limit: null, member_limit: null };
      return {
        ...prev,
        [projectId]: { ...baseline }
      };
    });
    setEditingProjects((prev) => ({ ...prev, [projectId]: false }));
  };

  const saveProject = async (projectId) => {
    setSaving(true);
    setError('');

    // Validate percentages don't exceed 100%
    const messagePercent = quotaPercentages.message[projectId]?.percentage || 0;
    const memberPercent = quotaPercentages.member[projectId]?.percentage || 0;

    if (messagePercent > 100) {
      setError('Total message quota allocation cannot exceed 100%');
      setSaving(false);
      return;
    }

    if (memberPercent > 100) {
      setError('Total member quota allocation cannot exceed 100%');
      setSaving(false);
      return;
    }
    try {
      const q = projectQuotas[projectId] || { message_limit: null, member_limit: null };
      const useInherentQuota = q.message_limit === null && q.member_limit === null;
      
      await devportalApi.updateProjectQuota(projectId, {
        message_limit: q.message_limit,
        member_limit: q.member_limit,
        useInherentQuota
      });
      await load();
      setEditingProjects((prev) => ({ ...prev, [projectId]: false }));
    } catch (e) {
      setError(e?.message || 'Failed to save project quota');
    } finally {
      setSaving(false);
    }
  };

  const saveAllProjects = async () => {
    setSaving(true);
    setError('');

    // Validate total percentages don't exceed 100%
    const totalMessagePercent = Object.values(quotaPercentages.message)
      .reduce((sum, {percentage}) => sum + percentage, 0);
    const totalMemberPercent = Object.values(quotaPercentages.member)
      .reduce((sum, {percentage}) => sum + percentage, 0);

    if (totalMessagePercent > 100) {
      setError('Total message quota allocation cannot exceed 100%');
      setSaving(false);
      return;
    }

    if (totalMemberPercent > 100) {
      setError('Total member quota allocation cannot exceed 100%');
      setSaving(false);
      return;
    }
    try {
      for (const projectId of projectIds) {
        const q = projectQuotas[projectId] || { message_limit: null, member_limit: null };
        const useInherentQuota = q.message_limit === null && q.member_limit === null;
        
        await devportalApi.updateProjectQuota(projectId, {
          message_limit: q.message_limit,
          member_limit: q.member_limit,
          useInherentQuota
        });
      }
      await load();
    } catch (e) {
      setError(e?.message || 'Failed to save project quotas');
    } finally {
      setSaving(false);
    }
  };

  const formatUsed = (used, limit, remains) => {
    if (used === null || used === undefined) return '-';
    if (limit === null || limit === undefined) return String(used);
    const remainsText = remains !== null && remains !== undefined ? ` (${remains} remaining)` : '';
    return `${used?.toLocaleString?.() || used} / ${limit?.toLocaleString?.() || limit}${remainsText}`;
  };

  const formatDateTime = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleString();
  };

  const toMs = (raw) => {
    if (raw === null || raw === undefined) return null;
    const n = Number(raw);
    if (!Number.isFinite(n)) return null;
    return n > 1e12 ? Math.floor(n / 1e6) : n;
  };

  const formatDuration = (raw) => {
    const ms = toMs(raw);
    if (ms === null) return raw === null || raw === undefined ? '-' : String(raw);
    const totalSec = Math.max(0, Math.floor(ms / 1000));
    const days = Math.floor(totalSec / 86400);
    const hours = Math.floor((totalSec % 86400) / 3600);
    const minutes = Math.floor((totalSec % 3600) / 60);
    const seconds = totalSec % 60;

    const secText = String(seconds).padStart(2, '0');
    if (days > 0) return `${days}d ${hours}h ${minutes}m ${secText}s`;
    if (hours > 0) return `${hours}h ${minutes}m ${secText}s`;
    return `${minutes}m ${secText}s`;
  };

  useEffect(() => {
    const t = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const liveQuotaRemainingMs = useMemo(() => {
    const endIso = tenantQuota?.quata_ended;
    if (endIso) {
      const end = new Date(endIso).getTime();
      if (!Number.isNaN(end)) return Math.max(0, end - nowTs);
    }

    const base = toMs(tenantQuota?.quata_remaining);
    if (base === null || quotaLoadedAtTs === null) return null;
    return Math.max(0, base - (nowTs - quotaLoadedAtTs));
  }, [nowTs, quotaLoadedAtTs, tenantQuota?.quata_ended, tenantQuota?.quata_remaining]);

  return (
    <div className="space-y-6">
      {/* Quota Inactive Warning */}
      {!hasActiveQuota && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 dark:bg-amber-900/20 dark:border-amber-800">
          <div className="flex items-start space-x-3">
            <Icon name="AlertTriangle" size={20} className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-amber-800 dark:text-amber-200 mb-2">
                No Active Quota
              </h3>
              <p className="text-amber-700 dark:text-amber-300 mb-4">
                You need an active quota plan to manage quota settings. Please purchase a license and quota plan to continue.
              </p>
              <div className="flex items-center space-x-3">
                <Button
                  variant="default"
                  iconName="CreditCard"
                  iconPosition="left"
                  onClick={() => navigate('/developer-portal', { state: { activeTab: 'plans-billing' } })}
                  className="bg-amber-600 hover:bg-amber-700 text-white border-amber-600"
                >
                  View Plans
                </Button>
                <Button
                  variant="outline"
                  iconName="ExternalLink"
                  iconPosition="left"
                  onClick={() => navigate('/pricing-plans')}
                  className="border-amber-600 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20"
                >
                  View Pricing Plans
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="bg-destructive/10 text-destructive p-4 rounded-lg flex items-center space-x-2">
          <Icon name="AlertCircle" size={20} />
          <span>{error}</span>
        </div>
      )}

      {/* Tenant Quota Section */}
      <div className={`bg-card rounded-lg border border-border p-6 ${!hasActiveQuota ? 'opacity-50 pointer-events-none' : ''}`}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Tenant Quota</h2>
            <p className="text-sm text-muted-foreground">
              Default limits for all projects unless a project override is set.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              iconName="RefreshCw"
              iconPosition="left"
              onClick={() => load()}
              disabled={loading || saving || !hasActiveQuota}
            >
              {loading ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            type="number"
            min={0}
            label="Message Limit"
            description="Total allowed message count for your tenant. Leave empty to keep backend default (if supported)."
            value={numberText(tenantQuota.message_limit)}
            onChange={(e) => setTenantQuota((s) => ({ ...s, message_limit: toNullableInt(e?.target?.value) }))}
            disabled
          />
          <Input
            type="number"
            min={0}
            label="Member Limit"
            description="Total allowed member count for your tenant. Leave empty to keep backend default (if supported)."
            value={numberText(tenantQuota.member_limit)}
            onChange={(e) => setTenantQuota((s) => ({ ...s, member_limit: toNullableInt(e?.target?.value) }))}
            disabled
          />
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 bg-muted/20 rounded-lg border border-border">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon name="Send" size={16} className="text-primary" />
                <span className="text-sm font-medium text-foreground">Messages Used</span>
              </div>
              <span className="text-sm text-muted-foreground">
                {formatUsed(tenantQuota.message_used, tenantQuota.message_limit, tenantQuota.message_remains)}
              </span>
            </div>
          </div>
          <div className="p-4 bg-muted/20 rounded-lg border border-border">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon name="Users" size={16} className="text-primary" />
                <span className="text-sm font-medium text-foreground">Members Used</span>
              </div>
              <span className="text-sm text-muted-foreground">
                {formatUsed(tenantQuota.member_used, tenantQuota.member_limit, tenantQuota.member_remains)}
              </span>
            </div>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-muted/20 rounded-lg border border-border">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon name="Clock" size={16} className="text-primary" />
                <span className="text-sm font-medium text-foreground">Quota Started</span>
              </div>
              <span className="text-sm text-muted-foreground">
                {formatDateTime(tenantQuota.quata_started)}
              </span>
            </div>
          </div>
          <div className="p-4 bg-muted/20 rounded-lg border border-border">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon name="Clock" size={16} className="text-primary" />
                <span className="text-sm font-medium text-foreground">Quota Ends</span>
              </div>
              <span className="text-sm text-muted-foreground">
                {formatDateTime(tenantQuota.quata_ended)}
              </span>
            </div>
          </div>
          <div className="p-4 bg-muted/20 rounded-lg border border-border">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon name="Clock" size={16} className="text-primary" />
                <span className="text-sm font-medium text-foreground">Time Remaining</span>
              </div>
              <span className="text-sm text-muted-foreground">
                {liveQuotaRemainingMs !== null ? formatDuration(liveQuotaRemainingMs) : formatDuration(tenantQuota.quata_remaining)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Project Quota Overrides Section */}
      <div className={`bg-card rounded-lg border border-border p-6 ${!hasActiveQuota ? 'opacity-50 pointer-events-none' : ''}`}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Project Quota Overrides</h2>
            <p className="text-sm text-muted-foreground">
              If an override is set, project cannot exceed that limit even if tenant quota is still available.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="text-sm text-muted-foreground">Loading quotas...</div>
        ) : projects?.length === 0 ? (
          <div className="text-sm text-muted-foreground">No projects found.</div>
        ) : (
          <div className="space-y-4">
            {projects.map((project) => {
              const id = project?.id;
              const q = projectQuotas[id] || { message_limit: null, member_limit: null };
              const inheritQuota = q.message_limit === null && q.member_limit === null;
              const isEditing = !!editingProjects[id];

              const messageSummary = inheritQuota
                ? `Inherit${tenantQuota.message_limit !== null && tenantQuota.message_limit !== undefined ? ` (${tenantQuota.message_limit})` : ''}`
                : `Override (${q.message_limit ?? '-'})`;
              const memberSummary = inheritQuota
                ? `Inherit${tenantQuota.member_limit !== null && tenantQuota.member_limit !== undefined ? ` (${tenantQuota.member_limit})` : ''}`
                : `Override (${q.member_limit ?? '-'})`;

              return (
                <div key={id} className="p-4 bg-muted/30 rounded-lg border border-border">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <Icon name="FolderOpen" size={16} className="text-primary" />
                        <h3 className="font-medium text-foreground">
                          {project?.title || project?.name}
                        </h3>
                      </div>
                      {project?.description && (
                        <p className="text-sm text-muted-foreground mt-1">
                          {project.description}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 justify-end">
                      {isEditing ? (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            iconName="X"
                            iconPosition="left"
                            onClick={() => cancelEditProject(id)}
                            disabled={saving}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="default"
                            size="sm"
                            iconName="Save"
                            iconPosition="left"
                            onClick={() => saveProject(id)}
                            disabled={saving}
                          >
                            Save
                          </Button>
                        </>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          iconName="Pencil"
                          iconPosition="left"
                          onClick={() => startEditProject(id)}
                          disabled={saving}
                        >
                          Edit
                        </Button>
                      )}
                    </div>
                  </div>

                  {isEditing ? (
                    <div className="mt-4 space-y-4">
                      {/* Single Inherit Checkbox */}
                      <div className="p-4 bg-muted/20 rounded-lg border border-border">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Icon name="Settings" size={16} className="text-primary" />
                            <span className="text-sm font-medium text-foreground">Quota Settings</span>
                          </div>
                          <Checkbox
                            checked={inheritQuota}
                            onChange={(e) => {
                              const checked = e?.target?.checked;
                              if (checked) {
                                // Inherit - set both to null
                                setProjectQuotaField(id, 'message_limit', null);
                                setProjectQuotaField(id, 'member_limit', null);
                              } else {
                                // Don't inherit - set to tenant defaults
                                setProjectQuotaField(id, 'message_limit', tenantQuota.message_limit ?? 0);
                                setProjectQuotaField(id, 'member_limit', tenantQuota.member_limit ?? 0);
                              }
                            }}
                            label="Inherit tenant quota limits"
                          />
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">
                          {inheritQuota 
                            ? "This project will inherit all tenant quota limits."
                            : "Set custom quota limits for this project."}
                        </p>
                      </div>

                      {/* Message and Member Limits */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div className="p-4 bg-background rounded-lg border border-border">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-foreground">Message Limit</span>
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                min={0}
                                value={numberText(q.message_limit)}
                                onChange={(e) =>
                                  setProjectQuotaField(id, 'message_limit', toNullableInt(e?.target?.value))
                                }
                                disabled={inheritQuota}
                                className="flex-1"
                              />
                              {tenantQuota.message_limit > 0 && (
                                <div className="text-sm text-muted-foreground w-16 text-right">
                                  {quotaPercentages.message[id]?.percentage || 0}%
                                </div>
                              )}
                            </div>
                            {tenantQuota.message_limit > 0 && (
                              <div className="text-xs text-muted-foreground">
                                Remaining: {quotaPercentages.message[id]?.remaining || 0} ({100 - (quotaPercentages.message[id]?.percentage || 0)}% of total)
                              </div>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-2">
                            When set, this project can use up to this amount of tenant quota.
                          </p>
                        </div>

                        <div className="p-4 bg-background rounded-lg border border-border">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-foreground">Member Limit</span>
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                min={0}
                                value={numberText(q.member_limit)}
                                onChange={(e) =>
                                  setProjectQuotaField(id, 'member_limit', toNullableInt(e?.target?.value))
                                }
                                disabled={inheritQuota}
                                className="flex-1"
                              />
                              {tenantQuota.member_limit > 0 && (
                                <div className="text-sm text-muted-foreground w-16 text-right">
                                  {quotaPercentages.member[id]?.percentage || 0}%
                                </div>
                              )}
                            </div>
                            {tenantQuota.member_limit > 0 && (
                              <div className="text-xs text-muted-foreground">
                                Remaining: {quotaPercentages.member[id]?.remaining || 0} ({100 - (quotaPercentages.member[id]?.percentage || 0)}% of total)
                              </div>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-2">
                            When set, this project cannot exceed this member cap.
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="p-3 bg-background rounded-lg border border-border">
                        <div className="flex justify-between items-center">
                          <div>
                            <div className="text-xs text-muted-foreground">Message Limit</div>
                            <div className="text-sm text-foreground font-medium">{messageSummary}</div>
                          </div>
                          {tenantQuota.message_limit > 0 && (
                            <div className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                              {quotaPercentages.message[id]?.percentage || 0}%
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="p-3 bg-background rounded-lg border border-border">
                        <div className="flex justify-between items-center">
                          <div>
                            <div className="text-xs text-muted-foreground">Member Limit</div>
                            <div className="text-sm text-foreground font-medium">{memberSummary}</div>
                          </div>
                          {tenantQuota.member_limit > 0 && (
                            <div className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                              {quotaPercentages.member[id]?.percentage || 0}%
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageQuota;
